import React, { useRef, useCallback, useMemo, useState } from 'react';
import { ListGroup, Button, Spinner, Form } from 'react-bootstrap';
import { FaTrash, FaEdit, FaChevronLeft, FaChevronRight, FaQuestionCircle } from 'react-icons/fa';
import './QnAList.css';
import ContentUnitTag from '../common/ContentUnitTag';
import QnAStatusTag from './QnAStatusTag';
import QnAQuestion from '../../models/QnAQuestion';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { usePaginatedTenantList } from '../../hooks/usePaginatedTenantList';
import { LAST_READ_MODULES } from '../../platform/lastRead/lastReadModules';
import { getQuestionActivityAt } from '../../platform/lastRead/lastReadUtils';
import { useUnreadItemIds } from '../../platform/lastRead/useUnreadItemIds';
import { QNA_STATUS } from '../../platform/qnaConstants';
import { canCreateContentForTenant } from '../../platform/membershipPermissions';
import { canEditQuestionContent } from '../../platform/qnaPermissions';
import {
  QUESTIONS_PAGE_SIZE,
  buildQuestionsPageQuery,
  deleteQuestion,
  fetchQuestionsPage,
} from '../../services/questionsService';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import {
  PREMIUM_MODULE_IDS,
  premiumListClassName,
  showListNavigateHint,
} from '../../platform/contentListVisual';
import ListItemNavigateHint from '../../platform/ListItemNavigateHint';

async function fetchQuestionsPageItems(user, tenantId, memberships, options) {
  const { questions, lastVisible } = await fetchQuestionsPage(user, tenantId, memberships, options);
  return { items: questions, lastVisible };
}

function getQuestionPreview(question) {
  const title = question?.title?.trim() || '';
  const body = question?.body?.trim() || '';
  if (body && body !== title) {
    return body.length > 140 ? `${body.slice(0, 140)}…` : body;
  }
  return null;
}

function QnAListTab({ active, children, onClick }) {
  return (
    <button
      type="button"
      className={`qna-list-tab${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function QnAList({ onSelectQuestion, isAdmin, onEditQuestion }) {
  const listTopRef = useRef(null);
  const [scopeFilter, setScopeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mineOnly, setMineOnly] = useState(false);

  const { user, loading } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();
  const isContentAdmin = canCreateContentForTenant(memberships, tenantId, user);

  const {
    items: questions,
    pageIndex,
    hasNextPage,
    pageLoading,
    goToPrevPage,
    goToNextPage,
    pageSize,
  } = usePaginatedTenantList({
    tenantId,
    user,
    memberships,
    userLoading: loading,
    pageSize: QUESTIONS_PAGE_SIZE,
    buildPageQuery: buildQuestionsPageQuery,
    fetchPage: fetchQuestionsPageItems,
    mapDoc: QnAQuestion.fromFirestore,
    logLabel: 'questions',
  });

  const effectiveScope = isContentAdmin && mineOnly ? 'mine' : scopeFilter;

  const filteredQuestions = useMemo(() => {
    return questions.filter((question) => {
      if (effectiveScope === 'mine' && question.uid !== user?.uid) {
        return false;
      }
      if (isContentAdmin && statusFilter !== 'all') {
        if (statusFilter === 'open' && question.status !== QNA_STATUS.OPEN) {
          return false;
        }
        if (statusFilter === 'answered') {
          const isHandled = question.status === QNA_STATUS.ANSWERED
            || question.status === QNA_STATUS.CLOSED;
          if (!isHandled) return false;
        }
      }
      return true;
    });
  }, [questions, effectiveScope, statusFilter, isContentAdmin, user?.uid]);

  const unreadQuestionIds = useUnreadItemIds(LAST_READ_MODULES.QNA, questions, {
    getActivityAt: getQuestionActivityAt,
  });

  const confirm = useConfirm();
  const toast = useToast();

  const scrollListIntoView = useCallback(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleDelete = async (questionId) => {
    const confirmed = await confirm({
      title: 'Izbriši vprašanje',
      message: 'Ali ste prepričani, da želite izbrisati to vprašanje?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteQuestion(questionId);
      toast.deleted('Vprašanje je bilo izbrisano.');
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Vprašanja ni bilo mogoče izbrisati.');
    }
  };

  const handlePrevPage = () => {
    if (goToPrevPage()) {
      scrollListIntoView();
    }
  };

  const handleNextPage = () => {
    if (goToNextPage()) {
      scrollListIntoView();
    }
  };

  const formatDate = (date) =>
    date?.toLocaleString('sl-SI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? '';

  const rangeStart = filteredQuestions.length ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = pageIndex * pageSize + filteredQuestions.length;
  const showPagination = pageIndex > 0 || hasNextPage;
  const showNavigateHint = showListNavigateHint(PREMIUM_MODULE_IDS.QNA);
  const listClassName = premiumListClassName(
    PREMIUM_MODULE_IDS.QNA,
    'qna-list platform-content-list',
  );
  const wrapperClassName = [
    'qna-list-wrapper',
    'platform-content-list-wrapper',
    showNavigateHint && 'platform-content-list-wrapper--card-stack',
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className="platform-content-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} ref={listTopRef}>
      <div className="qna-list-toolbar">
        <div className="qna-list-tabs" role="tablist" aria-label="Filtri vprašanj">
          {isContentAdmin ? (
            <>
              <QnAListTab
                active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
              >
                Vsa
              </QnAListTab>
              <QnAListTab
                active={statusFilter === 'answered'}
                onClick={() => setStatusFilter('answered')}
              >
                Odgovorjena
              </QnAListTab>
              <QnAListTab
                active={statusFilter === 'open'}
                onClick={() => setStatusFilter('open')}
              >
                Neodgovorjena
              </QnAListTab>
            </>
          ) : (
            <>
              <QnAListTab
                active={scopeFilter === 'all'}
                onClick={() => setScopeFilter('all')}
              >
                Vsa vprašanja
              </QnAListTab>
              <QnAListTab
                active={scopeFilter === 'mine'}
                onClick={() => setScopeFilter('mine')}
              >
                Moja vprašanja
              </QnAListTab>
            </>
          )}
        </div>

        {isContentAdmin && (
          <Form.Check
            type="switch"
            id="qna-mine-only"
            className="qna-list-mine-toggle"
            label="Samo moja"
            checked={mineOnly}
            onChange={(event) => setMineOnly(event.target.checked)}
          />
        )}
      </div>

      {pageLoading ? (
        <div className="platform-content-list-loading platform-content-list-loading--overlay">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Nalaganje...</span>
          </Spinner>
        </div>
      ) : !filteredQuestions.length && pageIndex === 0 ? (
        <div className={`platform-content-list-empty${showNavigateHint ? ' platform-content-list-empty--premium' : ''}`}>
          <strong>Ni vprašanj</strong>
          <span>Ni vprašanj za izbrani prikaz.</span>
        </div>
      ) : (
        <ListGroup className={listClassName}>
          {filteredQuestions.map((question) => {
            const canEdit = isAdmin || canEditQuestionContent(user, question);
            const isUnread = unreadQuestionIds.includes(question.id);
            const preview = getQuestionPreview(question);
            const showStatus = isContentAdmin || question.status !== QNA_STATUS.OPEN;
            const reserveActionsColumn = Boolean(user?.uid);

            return (
              <ListGroup.Item
                key={question.id}
                className={`qna-list-item platform-content-list-item${isUnread ? ' qna-list-item--unread platform-content-list-item--unread' : ''}`}
                onClick={() => onSelectQuestion(question)}
              >
                <div
                  className={`qna-list-item-main platform-content-list-item-main${reserveActionsColumn ? ' qna-list-item-main--has-actions platform-content-list-item-main--has-actions' : ''}`}
                >
                  <div className="qna-list-item-content platform-content-list-item-content">
                    <div className="platform-content-list-item-thumb platform-content-list-item-thumb--icon">
                      {isUnread && (
                        <span className="platform-content-list-item-unread-badge">
                          <span
                            className="platform-unread-dot"
                            title="Neprebrano"
                            aria-label="Neprebrano"
                          />
                        </span>
                      )}
                      <div className="platform-content-list-item-thumb-placeholder" aria-hidden="true">
                        <FaQuestionCircle />
                      </div>
                    </div>
                    <div className="qna-list-item-body platform-content-list-item-body">
                      <div className="qna-list-item-title platform-content-list-item-title">{question.title}</div>
                      {preview && (
                        <div className={`qna-list-item-preview platform-content-list-item-excerpt${showNavigateHint ? ' platform-content-list-item-excerpt--premium' : ''}`}>
                          {preview}
                        </div>
                      )}
                      <div className="platform-content-list-item-badges">
                        <ContentUnitTag item={question} />
                        {showStatus && <QnAStatusTag status={question.status} />}
                      </div>
                      <div className="qna-list-item-footer platform-content-list-item-footer">
                        <span>{question.displayName}</span>
                        <span className="qna-list-item-separator">·</span>
                        <span>{formatDate(question.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {reserveActionsColumn && (
                    <div className="qna-list-item-aside platform-content-list-item-aside">
                      <div className="platform-content-list-item-aside-inner">
                        {canEdit ? (
                          <div className="qna-list-item-actions platform-content-list-item-actions">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={(event) => {
                                event.stopPropagation();
                                onEditQuestion(question);
                              }}
                            >
                              <FaEdit /> Uredi
                            </Button>

                            {isAdmin && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDelete(question.id);
                                }}
                              >
                                <FaTrash /> Izbriši
                              </Button>
                            )}
                          </div>
                        ) : null}
                        {showNavigateHint ? <ListItemNavigateHint /> : null}
                      </div>
                    </div>
                  )}
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      {showPagination && (
        <nav className="platform-content-list-pagination" aria-label="Strani vprašanj">
          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handlePrevPage}
            disabled={pageIndex === 0 || pageLoading}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>Prejšnja</span>
          </Button>

          <div className="platform-content-list-pagination__info">
            <span className="platform-content-list-pagination__page">Stran {pageIndex + 1}</span>
            {filteredQuestions.length > 0 && (
              <span className="platform-content-list-pagination__range">
                Prikazano {rangeStart}–{rangeEnd}
              </span>
            )}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handleNextPage}
            disabled={!hasNextPage || pageLoading}
          >
            <span>Naslednja</span>
            <FaChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </div>
  );
}

QnAList.defaultProps = {
  onEditQuestion: () => {},
};

export default QnAList;
